import type { SettingsRecord } from './types';
import { normalizePhoneInput } from '../../lib/phone-input';

/** Size cap for settings images whose backend fields are still length-limited (staff photo, doctor signature). */
export const SETTINGS_RECORD_IMAGE_MAX_BYTES = 500 * 1024;

const GENERAL_SETTINGS_IMAGE_TYPES = new Set([
  'image/jpeg',
  'image/png',
  'image/webp',
]);

export const indianStateOptions = [
  'Andaman and Nicobar Islands (AN)',
  'Andhra Pradesh (AP)',
  'Arunachal Pradesh (AR)',
  'Assam (AS)',
  'Bihar (BR)',
  'Chandigarh (CH)',
  'Chhattisgarh (CG)',
  'Dadra and Nagar Haveli and Daman and Diu (DH)',
  'Delhi (DL)',
  'Goa (GA)',
  'Gujarat (GJ)',
  'Haryana (HR)',
  'Himachal Pradesh (HP)',
  'Jammu and Kashmir (JK)',
  'Jharkhand (JH)',
  'Karnataka (KA)',
  'Kerala (KL)',
  'Ladakh (LA)',
  'Lakshadweep (LD)',
  'Madhya Pradesh (MP)',
  'Maharashtra (MH)',
  'Manipur (MN)',
  'Meghalaya (ML)',
  'Mizoram (MZ)',
  'Nagaland (NL)',
  'Odisha (OD)',
  'Puducherry (PY)',
  'Punjab (PB)',
  'Rajasthan (RJ)',
  'Sikkim (SK)',
  'Tamil Nadu (TN)',
  'Telangana (TS)',
  'Tripura (TR)',
  'Uttar Pradesh (UP)',
  'Uttarakhand (UK)',
  'West Bengal (WB)',
].map((state) => ({ value: state, label: state }));

export interface GeneralSettingsFormValues {
  title: string;
  email: string;
  address: string;
  state: string;
  hfId: string;
  hmisId: string;
  phone: string;
  tdsPercent: string;
  logoDataUrl: string;
  smallLogoDataUrl: string;
  letterHeadImageDataUrl: string;
  rateLimiterMaxRequests: string;
  gzipCompressionLevel: string;
}

export type GeneralSettingsFormErrors = Partial<Record<keyof GeneralSettingsFormValues, string>>;
export type GeneralSettingsImageReadingState = Record<'logoDataUrl' | 'smallLogoDataUrl' | 'letterHeadImageDataUrl', boolean>;

export const isGeneralSettingsImageReading = (state: GeneralSettingsImageReadingState) =>
  state.logoDataUrl || state.smallLogoDataUrl || state.letterHeadImageDataUrl;

const stringValue = (value: unknown) => typeof value === 'string' ? value : '';
const numberValue = (value: unknown, fallback: number) => typeof value === 'number' && Number.isFinite(value)
  ? String(value)
  : String(fallback);

const nestedNumber = (record: SettingsRecord | null | undefined, group: string, field: string, fallback: number) => {
  const value = record?.[group];
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return String(fallback);
  return numberValue((value as Record<string, unknown>)[field], fallback);
};

export const makeGeneralSettingsFormValues = (
  record: SettingsRecord | null | undefined,
): GeneralSettingsFormValues => ({
  title: stringValue(record?.title),
  email: stringValue(record?.email),
  address: stringValue(record?.address),
  state: stringValue(record?.state),
  hfId: stringValue(record?.hfId),
  hmisId: stringValue(record?.hmisId),
  phone: normalizePhoneInput(stringValue(record?.phone)),
  tdsPercent: numberValue(record?.tdsPercent, 0),
  logoDataUrl: stringValue(record?.logoDataUrl),
  smallLogoDataUrl: stringValue(record?.smallLogoDataUrl),
  letterHeadImageDataUrl: stringValue(record?.letterHeadImageDataUrl),
  rateLimiterMaxRequests: nestedNumber(record, 'rateLimiter', 'maxRequests', 500),
  gzipCompressionLevel: nestedNumber(record, 'gzipCompression', 'level', 6),
});

const requiredMessage = (label: string) => `${label} is required.`;

export const validateGeneralSettingsForm = (values: GeneralSettingsFormValues): GeneralSettingsFormErrors => {
  const errors: GeneralSettingsFormErrors = {};
  const requiredFields = [
    ['title', 'Title'],
    ['email', 'Email'],
    ['address', 'Address'],
    ['state', 'State'],
    ['phone', 'Phone number'],
  ] as const;

  requiredFields.forEach(([field, label]) => {
    if (!values[field].trim()) errors[field] = requiredMessage(label);
  });

  if (values.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email.trim())) {
    errors.email = 'Enter a valid email address.';
  }
  if (values.phone.trim() && !/^\d{10}$/.test(values.phone.trim())) {
    errors.phone = 'Enter a 10 digit phone number.';
  }

  const tdsPercent = values.tdsPercent === '' ? 0 : Number(values.tdsPercent);
  if (!Number.isFinite(tdsPercent) || tdsPercent < 0 || tdsPercent > 100) {
    errors.tdsPercent = 'TDS must be between 0 and 100.';
  }

  if (values.rateLimiterMaxRequests !== '') {
    const maxRequests = Number(values.rateLimiterMaxRequests);
    if (!Number.isInteger(maxRequests) || maxRequests < 1 || maxRequests > 1_000_000) {
      errors.rateLimiterMaxRequests = 'Rate limiter count must be a whole number from 1 to 1,000,000.';
    }
  }

  if (values.gzipCompressionLevel !== '') {
    const gzipLevel = Number(values.gzipCompressionLevel);
    if (!Number.isInteger(gzipLevel) || gzipLevel < -1 || gzipLevel > 9) {
      errors.gzipCompressionLevel = 'Gzip compression level must be a whole number from -1 to 9.';
    }
  }

  return errors;
};

export const buildGeneralSettingsPayload = (values: GeneralSettingsFormValues) => ({
  title: values.title.trim(),
  email: values.email.trim(),
  address: values.address.trim(),
  state: values.state,
  hfId: values.hfId.trim() || null,
  hmisId: values.hmisId.trim() || null,
  phone: normalizePhoneInput(values.phone),
  tdsPercent: values.tdsPercent === '' ? 0 : Number(values.tdsPercent),
  logoDataUrl: values.logoDataUrl || null,
  smallLogoDataUrl: values.smallLogoDataUrl || null,
  letterHeadImageDataUrl: values.letterHeadImageDataUrl || null,
  rateLimiter: {
    maxRequests: values.rateLimiterMaxRequests === '' ? 500 : Number(values.rateLimiterMaxRequests),
  },
  gzipCompression: {
    level: values.gzipCompressionLevel === '' ? 6 : Number(values.gzipCompressionLevel),
  },
});

export const validateGeneralSettingsImage = (file: Pick<File, 'size' | 'type'>, maxBytes?: number): string | null => {
  if (!GENERAL_SETTINGS_IMAGE_TYPES.has(file.type)) return 'Use a PNG, JPEG, or WebP image.';
  if (maxBytes !== undefined && file.size > maxBytes) return `Image must be ${Math.round(maxBytes / 1024)} KB or smaller.`;
  return null;
};

export const readGeneralSettingsImage = (file: File, maxBytes?: number): Promise<string> => {
  const validationError = validateGeneralSettingsImage(file, maxBytes);
  if (validationError) return Promise.reject(new Error(validationError));

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('The image could not be read. Please choose it again.'));
    reader.onabort = () => reject(new Error('Image reading was cancelled. Please choose it again.'));
    reader.onload = () => {
      if (typeof reader.result === 'string') resolve(reader.result);
      else reject(new Error('The image could not be read. Please choose it again.'));
    };
    reader.readAsDataURL(file);
  });
};
